import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, Maximize2, Minimize2 } from 'lucide-react';
import { useTheme } from '@/shared/lib/theme';
import {
  METABASE_IFRAME_HEIGHT_CLASS,
  METABASE_IFRAME_SKELETON_CLASS,
} from '@/features/dashboard/model/metabase-embed-height';
import { withMetabaseTheme } from '@/features/dashboard/model/metabase-theme';
import { cn } from '@/shared/lib/cn';
import { EmptyState } from '@/shared/components/empty-state';
import { Skeleton } from '@/shared/ui/skeleton';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import { useMetabaseEmbed } from '../api/use-metabase-embed';

interface MetabaseEmbedPanelProps {
  idPassbook: string;
}

function getFullscreenElement(): Element | null {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
  };
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

async function requestFs(el: HTMLElement): Promise<boolean> {
  const anyEl = el as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };
  try {
    if (typeof el.requestFullscreen === 'function') {
      await el.requestFullscreen();
      return true;
    }
    if (typeof anyEl.webkitRequestFullscreen === 'function') {
      await anyEl.webkitRequestFullscreen();
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

async function exitFs(): Promise<void> {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
    webkitFullscreenElement?: Element | null;
  };
  try {
    if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
      await document.exitFullscreen();
      return;
    }
    if (doc.webkitFullscreenElement && typeof doc.webkitExitFullscreen === 'function') {
      await doc.webkitExitFullscreen();
    }
  } catch {
    /* ignore */
  }
}

export function MetabaseEmbedPanel({ idPassbook }: MetabaseEmbedPanelProps) {
  const { resolvedTheme } = useTheme();
  const { data, isLoading, isError, error } = useMetabaseEmbed(idPassbook, true);
  const [activeTab, setActiveTab] = useState('0');
  /** Fullscreen nativo (Fullscreen API). */
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  /** Fallback CSS — iOS/Safari mobile não suportam requestFullscreen em div. */
  const [cssFullscreen, setCssFullscreen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const isFullscreen = nativeFullscreen || cssFullscreen;

  // Paridade com legacy dashboard_v2_metabase.ejs: abas só se length > 1;
  // com 1 dashboard usa iframeUrl (primary), que já aplica lojaIndividual.
  const frames = useMemo(() => {
    if (!data) return [];
    if (data.arrIframes?.length > 1) return data.arrIframes;
    return [{ iframeUrl: data.iframeUrl, dashboard: 'main', aba: 'Painel' }];
  }, [data]);

  const themedFrames = useMemo(
    () => frames.map((f) => ({ ...f, src: withMetabaseTheme(f.iframeUrl, resolvedTheme) })),
    [frames, resolvedTheme],
  );

  useEffect(() => {
    const sync = () => setNativeFullscreen(getFullscreenElement() === panelRef.current);
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  useEffect(() => {
    if (!cssFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [cssFullscreen]);

  useEffect(() => {
    if (!cssFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCssFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cssFullscreen]);

  const toggleFullscreen = useCallback(async () => {
    const el = panelRef.current;
    if (!el) return;

    if (isFullscreen) {
      if (nativeFullscreen) await exitFs();
      setCssFullscreen(false);
      return;
    }

    const ok = await requestFs(el);
    if (!ok) {
      setCssFullscreen(true);
    }
  }, [isFullscreen, nativeFullscreen]);

  if (isLoading) {
    return <Skeleton className={cn('w-full rounded-2xl', METABASE_IFRAME_SKELETON_CLASS)} />;
  }

  if (isError || !data) {
    return (
      <EmptyState
        icon={BarChart3}
        title="BI indisponível"
        description={error instanceof Error ? error.message : 'Não foi possível carregar o Metabase.'}
        className="py-20"
      />
    );
  }

  const current = themedFrames[Number(activeTab)] ?? themedFrames[0];

  return (
    <div
      ref={panelRef}
      className={cn(
        'flex flex-col gap-3',
        isFullscreen && 'h-full min-h-full bg-background p-3 sm:p-4',
        cssFullscreen && 'fixed inset-0 z-[70] h-[100dvh] w-full overflow-hidden',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        {themedFrames.length > 1 ? (
          <SegmentedControl
            value={activeTab}
            onChange={setActiveTab}
            className="max-w-full flex-1 flex-wrap"
            options={themedFrames.map((frame, index) => ({
              value: String(index),
              label: frame.aba ?? `Dashboard ${frame.dashboard}`,
            }))}
          />
        ) : (
          <span className="text-sm text-muted-foreground">{current.aba ?? 'BI Avançado'}</span>
        )}

        <Tooltip content={isFullscreen ? 'Sair da tela cheia (Esc)' : 'Abrir em tela cheia'} side="left">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={toggleFullscreen}
            aria-pressed={isFullscreen}
            aria-label={isFullscreen ? 'Sair da tela cheia' : 'Abrir BI em tela cheia'}
          >
            {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            {isFullscreen ? 'Sair' : 'Tela cheia'}
          </Button>
        </Tooltip>
      </div>

      <div
        className={cn(
          'glass rounded-2xl',
          isFullscreen && 'flex min-h-0 flex-1 flex-col overflow-hidden',
        )}
      >
        <iframe
          key={`${current.dashboard}-${resolvedTheme}`}
          src={current.src}
          title={current.aba ?? 'Dashboard Metabase'}
          className={cn(
            'w-full border-0 bg-background',
            isFullscreen ? 'h-full min-h-0 flex-1' : METABASE_IFRAME_HEIGHT_CLASS,
          )}
          allow="fullscreen"
          allowFullScreen
        />
      </div>
    </div>
  );
}
